import { useLoginInfo } from '@/components/LoginInfo/provider';
import type { ReportActionState } from '@/features/admin_invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonGroup,
  ButtonV2,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, MouseEvent, memo, useCallback } from 'react';
import styles from './AdminInvoiceReportDrawerHeaderActions.module.scss';

const cx = classnames.bind(styles);

export type AdminInvoiceReportDrawerHeaderActionsProps = {
  detailPath: string;
  editPath: string;
  editAction?: ReportActionState;
  cancelAction?: ReportActionState;
  deleteAction?: ReportActionState;
  handleCancel?: () => void;
  handleDelete?: () => void;
};

export const AdminInvoiceReportDrawerHeaderActions: FC<AdminInvoiceReportDrawerHeaderActionsProps> =
  memo(
    ({
      detailPath,
      editPath,
      editAction,
      cancelAction,
      deleteAction,
      handleCancel,
      handleDelete,
    }) => {
      const { t } = useTranslation();
      const { currentOfficeMember } = useLoginInfo();
      const isObserver =
        currentOfficeMember?.officeMemberSetting?.role === 'observer';
      const container = useGlobalContainerRef();

      const handleDetailClick = useCallback(() => {
        window.location.href = detailPath;
      }, [detailPath]);

      const handleEditClick = useCallback(() => {
        if (editPath) window.location.href = editPath;
      }, [editPath]);

      const handleStopPropagation = useCallback(
        (e: MouseEvent<HTMLDivElement>) => e.stopPropagation(),
        []
      );

      const isEditDisabled = !editAction?.allowed;
      const isCancelDisabled = !cancelAction?.allowed;
      const isDeleteDisabled = !deleteAction?.allowed;

      return (
        <div
          className={cx('actionBar')}
          onClick={handleStopPropagation}
          data-testid='admin-invoice-report-drawer-header-actions'
        >
          <ButtonGroup>
            {cancelAction?.visible &&
              (isCancelDisabled && cancelAction.reason ? (
                <Tooltip
                  title={cancelAction.reason}
                  overlayClassName={cx('cancelTooltip')}
                  getPopupContainer={container}
                >
                  <span className={cx('dangerButton')}>
                    <ButtonV2 size='sm' isTransparent color='danger' isDisabled>
                      {t('ap_report_status_canceled')}
                    </ButtonV2>
                  </span>
                </Tooltip>
              ) : (
                <span className={cx('dangerButton')}>
                  <ButtonV2
                    size='sm'
                    color='danger'
                    isDisabled={isCancelDisabled}
                    isTransparent
                    onClick={handleCancel}
                  >
                    {t('ap_report_status_canceled')}
                  </ButtonV2>
                </span>
              ))}
            {deleteAction?.visible &&
              (isDeleteDisabled && deleteAction.reason ? (
                <Tooltip
                  title={deleteAction.reason}
                  overlayClassName={cx('cancelTooltip')}
                  getPopupContainer={container}
                >
                  <span className={cx('dangerButton')}>
                    <ButtonV2 size='sm' isTransparent color='danger' isDisabled>
                      {t('admin_invoice_reports.actions.delete')}
                    </ButtonV2>
                  </span>
                </Tooltip>
              ) : (
                <span className={cx('dangerButton')}>
                  <ButtonV2
                    size='sm'
                    color='danger'
                    isDisabled={isDeleteDisabled}
                    isTransparent
                    onClick={handleDelete}
                  >
                    {t('admin_invoice_reports.actions.delete')}
                  </ButtonV2>
                </span>
              ))}
            {editAction?.visible && !isEditDisabled && !isObserver && (
              <ButtonV2 size='sm' isTertiary onClick={handleEditClick}>
                {t('edit')}
              </ButtonV2>
            )}
            <ButtonV2 size='sm' isSecondary onClick={handleDetailClick}>
              {t('admin_invoice_reports.table.columns.detail')}
            </ButtonV2>
          </ButtonGroup>
        </div>
      );
    }
  );

AdminInvoiceReportDrawerHeaderActions.displayName =
  'AdminInvoiceReportDrawerHeaderActions';
